// dataLayer helpers for the signup funnel.
//
// The signup flow is a Vue SPA (packs/login.js) using createWebHistory, so after
// the initial server-rendered page load there are no further page loads for GTM
// to hook. GTM's built-in "All Pages" trigger fires exactly once; every step
// after that has to be reported explicitly through these helpers.
//
// All functions are safe no-ops when GTM is absent (any non-production
// environment, or an ad blocker), so signup must never break because a push
// failed.

const CONVERSION_EVENT_TIMEOUT_MS = 2000;

export function pushToDataLayer(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch (_e) {
    /* tracking is best-effort and must never interrupt signup */
  }
}

// Fired on each step of the signup wizard. `step` is the Vuex selectedStep value
// (e.g. 'sign-up-create-account'), which doubles as the component name.
export function trackSignupStep(step) {
  if (!step) return;
  pushToDataLayer({ event: 'signup_step_viewed', signup_step: step });
}

// The conversion event. This is what a Google Ads conversion tag should trigger
// on. Fired once, immediately after the account is created (POST /auth returns
// success) and before the user is moved to the next step.
//
// Deliberately carries no email/name/phone -- the Ads conversion is matched via
// the gclid carried in the shared GTM container's cookies, not via PII.
export function trackSignupCompleted(
  { accountType, plan, signupFlow } = {},
  onComplete
) {
  const event = {
    event: 'sign_up',
    account_type: accountType || null,
    plan: plan || null,
    signup_flow: signupFlow || null,
  };

  if (typeof onComplete !== 'function') {
    pushToDataLayer(event);
    return;
  }

  if (typeof window === 'undefined') {
    onComplete();
    return;
  }

  const gtmState = window.__coachCatalystGtmState;
  if (gtmState !== 'loading' && gtmState !== 'loaded') {
    // GTM is disabled in this environment or its script was blocked.
    pushToDataLayer(event);
    onComplete();
    return;
  }

  let completed = false;
  const complete = () => {
    if (completed) return;
    completed = true;
    window.clearTimeout(fallback);
    onComplete();
  };
  // GTM calls eventCallback after matching tags finish, or at eventTimeout.
  // The local fallback also covers ad blockers and a container that failed to
  // load, where no one would consume the queued event.
  const fallback = window.setTimeout(
    complete,
    CONVERSION_EVENT_TIMEOUT_MS + 100
  );

  pushToDataLayer({
    ...event,
    eventCallback: complete,
    eventTimeout: CONVERSION_EVENT_TIMEOUT_MS,
  });
}
